/**
 * Capa de edificios (cobertura vertical).
 *
 * Mismo patrón que la capa de DEA: un punto azul con la ubicación exacta y un
 * pictograma que se arrastra por separado, unidos por una guía punteada.
 *
 * La edición se captura con `window.prompt()` encadenados. Es la peor parte de
 * la UX heredada y está anotada en docs/hallazgos.md; todo el parseo vive aparte
 * en `domain/levels.js` con tests propios, así que reemplazar los prompts por un
 * formulario no toca ninguna regla.
 */

import L from 'leaflet';
import { destinationPoint } from '../domain/geo.js';
import {
  normalizeShared,
  parseLevelNames,
  parseLevelsWithCount,
  parseSharedLevels,
} from '../domain/levels.js';
import { missingDeaCount, verticalApplies } from '../domain/compliance.js';
import { buildingNumberIcon, buildingPointIcon } from './icons.js';

const tooltipFor = (building) => {
  if (!verticalApplies(building)) return `${building.nombre} · sin exigencia vertical`;
  const missing = missingDeaCount(building);
  return `${building.nombre} · ${missing ? `NO CUMPLE (faltan ${missing})` : 'CUMPLE'}`;
};

/** Pregunta un texto con valor por defecto. `null` si el operador cancela. */
const ask = (question, fallback = '') => window.prompt(question, fallback);

export class BuildingLayer {
  constructor(map, { onChange, onCommit }) {
    this.map = map;
    this.onChange = onChange;
    this.onCommit = onCommit;
    this.items = [];
  }

  get length() {
    return this.items.length;
  }

  /** Datos planos para el dominio, la lámina y el guardado. */
  snapshot() {
    return this.items.map((building) => {
      const center = building.marker.getLatLng();
      const icon = building.icono.getLatLng();
      return {
        lat: center.lat,
        lng: center.lng,
        pisos: building.pisos,
        sub: building.sub,
        num: building.num,
        nombre: building.nombre,
        tiene: building.tiene || [],
        cant: building.cant || {},
        ext: building.ext || [],
        noPub: building.noPub || [],
        compartidos: building.compartidos || {},
        pisoNombres: building.pisoNombres || {},
        icono: [icon.lat, icon.lng],
      };
    });
  }

  /**
   * Captura los datos de un edificio.
   * Devuelve `null` si el operador cancela en el primer paso.
   */
  #askData(current = null) {
    const isNew = !current;

    const floors = Number.parseInt(
      ask('N° de pisos sobre rasante:', isNew ? '8' : String(current.pisos)),
      10,
    );
    if (!floors || floors < 1) return null;

    const basements = Math.max(
      0,
      Number.parseInt(ask('N° de subterráneos (0 si no tiene):', isNew ? '0' : String(current.sub || 0)), 10) || 0,
    );

    const fallbackName = isNew ? `Torre ${this.items.length + 1}` : current.nombre;
    const name = ask('Nombre del edificio:', fallbackName) || fallbackName;

    const levelHelp =
      'Niveles: N1, N2… pisos · N-1, N-2… subterráneos. Varios equipos en un nivel: 1x2';

    const installedText = ask(
      `¿En qué niveles hay DEA DEL CLIENTE?\n${levelHelp}\nVacío = ninguno:`,
      isNew ? '' : this.#formatLevels(current),
    );
    const { levels: installed, cant } = parseLevelsWithCount(
      installedText === null ? '' : installedText,
      floors,
    );

    const externalText = ask(
      `¿En qué niveles hay DEA EXTERNO (de un tercero)?\nCubre sólo su propio nivel.\nVacío = ninguno:`,
      isNew ? '' : (current.ext || []).map((n) => `N${n}`).join(', '),
    );
    const external = parseLevelsWithCount(externalText === null ? '' : externalText, floors).levels;

    const noPublicText = ask(
      `¿Qué niveles NO tienen uso público? (bodega, sala de máquinas…)\nNo generan exigencia.\nVacío = ninguno:`,
      isNew ? '' : (current.noPub || []).map((n) => `N${n}`).join(', '),
    );
    const noPublic = parseLevelsWithCount(noPublicText === null ? '' : noPublicText, floors).levels;

    const sharedText = ask(
      '¿Qué niveles comparten el DEA con un equipo de terreno? (ej: N3)\nVacío = ninguno:',
      isNew ? '' : Object.keys(current.compartidos || {}).map((n) => `N${n}`).join(', '),
    );
    const shared = this.#askSharedDetail(sharedText, installed, current?.compartidos || {});

    const namesText = ask(
      'Nombre del lugar por nivel (ej: N1=Acceso, N4=Casino).\nSi un nivel tiene varios equipos, repetí el nivel.\nOpcional:',
      isNew ? '' : this.#formatNames(current),
    );
    const names = parseLevelNames(namesText === null ? '' : namesText, installed);

    return {
      pisos: floors,
      sub: basements,
      nombre: name,
      tiene: installed,
      cant,
      ext: external,
      noPub: noPublic,
      compartidos: shared,
      pisoNombres: names,
    };
  }

  /** Reconstruye "N1x2, N4" a partir de los datos guardados, para preseleccionar el prompt. */
  #formatLevels(building) {
    return (building.tiene || [])
      .map((level) => {
        const count = (building.cant || {})[level];
        return count > 1 ? `N${level}x${count}` : `N${level}`;
      })
      .join(', ');
  }

  #formatNames(building) {
    return Object.entries(building.pisoNombres || {})
      .flatMap(([level, labels]) =>
        (Array.isArray(labels) ? labels : [labels]).map((label) => `N${level}=${label}`),
      )
      .join(', ');
  }

  /** Por cada nivel compartido, pregunta con qué DEA de terreno comparte equipo. */
  #askSharedDetail(sharedText, installed, previous) {
    if (sharedText === null) return normalizeShared(previous);
    const levels = Object.keys(parseSharedLevels(sharedText, installed)).map(Number);
    const shared = {};
    levels.forEach((level) => {
      const answer = ask(
        `El DEA del nivel N${level} ¿es cuál DEA de terreno? (escribe el número, ej: 1):`,
        String(previous[level] || ''),
      );
      shared[level] = answer ? Number.parseInt(answer.trim(), 10) || null : null;
    });
    return shared;
  }

  /** Pregunta los datos de un edificio nuevo y lo crea en el centro del mapa. */
  promptCreate() {
    const data = this.#askData();
    if (!data) return null;
    const center = this.map.getCenter();
    return this.add(center.lat, center.lng, data);
  }

  add(lat, lng, data) {
    const number = data.num ?? this.items.length + 1;
    const center = L.latLng(lat, lng);

    const marker = L.marker(center, {
      draggable: true,
      zIndexOffset: 700,
      icon: buildingPointIcon(),
    }).addTo(this.map);
    marker.bindTooltip('Ubicación del edificio', { direction: 'top' });

    const building = {
      marker,
      num: number,
      pisos: data.pisos,
      sub: data.sub || 0,
      nombre: data.nombre || `Torre ${number}`,
      tiene: data.tiene || [],
      cant: data.cant || {},
      ext: data.ext || [],
      noPub: data.noPub || [],
      compartidos: normalizeShared(data.compartidos),
      pisoNombres: data.pisoNombres || {},
    };

    const iconPosition = data.icono
      ? L.latLng(data.icono[0], data.icono[1])
      : L.latLng(...destinationPoint(lat, lng, 26, 180));
    const icono = L.marker(iconPosition, {
      draggable: true,
      zIndexOffset: 720,
      icon: buildingNumberIcon(this.items.length + 1),
    }).addTo(this.map);
    icono.bindTooltip(tooltipFor(building), { direction: 'right', offset: [12, 0] });

    const guia = L.polyline([center, iconPosition], {
      color: '#fff',
      weight: 1.5,
      dashArray: '4 3',
    }).addTo(this.map);

    building.icono = icono;
    building.guia = guia;

    const edit = () => this.#promptEdit(building);
    icono.on('click', edit);
    marker.on('click', edit);

    let offsetLat = iconPosition.lat - lat;
    let offsetLng = iconPosition.lng - lng;
    marker.on('dragstart', () => {
      const point = marker.getLatLng();
      const iconAt = icono.getLatLng();
      offsetLat = iconAt.lat - point.lat;
      offsetLng = iconAt.lng - point.lng;
    });
    marker.on('drag', (event) => {
      const point = event.latlng;
      const target = L.latLng(point.lat + offsetLat, point.lng + offsetLng);
      icono.setLatLng(target);
      guia.setLatLngs([point, target]);
    });
    marker.on('dragend', () => this.onCommit());
    icono.on('drag', (event) => guia.setLatLngs([marker.getLatLng(), event.latlng]));
    icono.on('dragend', () => this.onCommit());

    this.items.push(building);
    this.renumber();
    this.onChange();
    return building;
  }

  #promptEdit(building) {
    const data = this.#askData(building);
    if (!data) return;
    Object.assign(building, data);
    building.icono.setTooltipContent(tooltipFor(building));
    this.onChange();
    this.onCommit();
  }

  remove(num) {
    const index = this.items.findIndex((building) => building.num === num);
    if (index < 0) return;
    this.#destroy(this.items[index]);
    this.items.splice(index, 1);
    this.renumber();
    this.onChange();
  }

  clear() {
    this.items.forEach((building) => this.#destroy(building));
    this.items.length = 0;
  }

  #destroy(building) {
    [building.marker, building.icono, building.guia].forEach((layer) => {
      if (layer) this.map.removeLayer(layer);
    });
  }

  renumber() {
    this.items.forEach((building, index) => {
      building.num = index + 1;
      if (building.icono?.setIcon) {
        building.icono.setIcon(buildingNumberIcon(index + 1));
        building.icono.setTooltipContent(tooltipFor(building));
      }
    });
  }
}
